/**
 * AppAboutModal — Surface Layer (/01)
 *
 * A local, Carbon-compliant "About" dialog modeled on the IBM Products
 * `AboutModal` pattern (https://pages.github.ibm.com/carbon/ibm-products/components/AboutModal/usage/).
 * It composes the real `@carbon/ibm-products` `AboutModal` and surfaces live,
 * SAFE runtime metadata (version, schema, service health, local inference
 * routing) read from `useStatus()`. It NEVER renders secrets, tokens, or
 * filesystem paths.
 *
 * The component is presentational over a tiny prop surface
 * (`open`, `onClose`, `plane`) and reads its own status internally so the
 * integrator can drop it into either plane with a single flag. The DevHub
 * variant additionally exposes a small set of runtime readiness booleans;
 * the UserHub variant shows the user-safe subset only.
 *
 * Accessibility: the underlying AboutModal manages focus, the close affordance
 * (`closeIconDescription`) and `modalAriaLabel`. Definition-list metadata uses
 * the shared `.surface-fields` / `.surface-field` semantics from `views.scss`.
 */

import type { ReactNode } from 'react';
import { Link } from '@carbon/react';
import { AboutModal } from '@carbon/ibm-products';
import { Application } from '@carbon/icons-react';
import { useStatus } from '@lib/persistence/useRuntimeData';
import '../../styles/views.scss';
import './about-modal.scss';

const COPYRIGHT_TEXT = 'Copyright © 2026 Cestari Studio';
const PRIVACY_SUMMARY =
  'Local-first. No corpus upload without approval. Same-origin BFF with user-JWT + RLS.';

export interface AppAboutModalProps {
  /** Controls visibility; the AboutModal owns its own mount/unmount. */
  open: boolean;
  /** Invoked when the user dismisses the dialog. */
  onClose: () => void;
  /** Which surface plane is asking — gates the runtime-flags detail group. */
  plane: 'devhub' | 'userhub';
}

/** Renders one definition-list row; omits nothing — callers pass safe values. */
function MetaField({ term, value }: { term: string; value: ReactNode }): JSX.Element {
  return (
    <div className="surface-field">
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export function AppAboutModal({ open, onClose, plane }: AppAboutModalProps): JSX.Element {
  const { status } = useStatus();

  const version = status?.version.api ?? 'unknown';
  const schemaVersion = status?.version.schema ?? 'unknown';
  const authState = status?.services.auth ?? 'unknown';
  const dbState = status?.services.db ?? 'unknown';
  const routingDecision = status?.local_inference.routing_decision ?? 'unknown';

  const isDevHub = plane === 'devhub';

  const logo: ReactNode = (
    <span className="about-modal__logo" aria-hidden="true">
      <Application size={24} />
    </span>
  );

  // AboutModal wraps `content` in its own <p>; pass the text as a string (not a <p>) to avoid
  // an invalid <p>-in-<p> nesting (validateDOMNesting warning on every render).
  const content: ReactNode = PRIVACY_SUMMARY;

  const renderFlag = (ready: boolean | undefined): string => {
    if (ready === undefined) return 'unknown';
    return ready ? 'ready' : 'not ready';
  };

  const additionalInfo: ReactNode = (
    <>
      <dl className="surface-fields">
        <MetaField term="API version" value={version} />
        <MetaField term="Schema version" value={schemaVersion} />
        <MetaField term="Auth" value={authState} />
        <MetaField term="Database" value={dbState} />
        <MetaField term="Local inference" value={routingDecision} />
      </dl>
      {isDevHub ? (
        <div className="about-modal__flags">
          <p className="about-modal__flags-title">Runtime readiness</p>
          <dl className="surface-fields">
            <MetaField
              term="Granite 4.1"
              value={renderFlag(status?.local_inference.granite_4_1_ready)}
            />
            <MetaField
              term="Chat worker"
              value={renderFlag(status?.local_inference.chat_worker_ready)}
            />
            <MetaField
              term="ActionFrame-only"
              value={renderFlag(status?.local_inference.actionframe_only)}
            />
          </dl>
        </div>
      ) : null}
    </>
  );

  const links: ReactNode[] = [
    <Link key="support" href="/support">
      Support
    </Link>,
  ];

  return (
    <AboutModal
      open={open}
      onClose={onClose}
      title="Cestari Studio"
      version={version}
      copyrightText={COPYRIGHT_TEXT}
      closeIconDescription="Close about"
      logo={logo}
      content={content}
      additionalInfo={additionalInfo}
      links={links}
      modalAriaLabel="About Cestari Studio"
    />
  );
}
